import { all, call, cancelled, put, select, takeEvery, takeLeading } from 'typed-redux-saga'
import { Action, Actions } from '~/actions'
import { loadTranslations } from '~/components/Locale'
import {
	ProductsDocument,
	UpsellCartAddDocument,
	UpsellCartRemoveDocument,
	UpsellCheckoutCreateDocument,
	UpsellDocument
} from '~/gql'
import type { State } from '~/types'
import { AukError, extractUpsells, getCurrentLanguage, getDiscountValue, isError, isOk, shopifyFetch } from '~/utils'

export function* productSagas() {
	yield* takeLeading('PRODUCTS_LOAD', loadProducts)
	yield* takeEvery('UPSELL_LOAD', loadUpsells)
	yield* takeEvery('UPSELL_ADD', onUpsellChange)
	yield* takeEvery('UPSELL_REMOVE', onUpsellChange)
}

function* loadProducts(action: Action<'PRODUCTS_LOAD'>) {
	let error: AukError | undefined
	try {
		const res = yield* call(() => shopifyFetch(ProductsDocument, {}))
		if (isOk(res)) {
			return yield* put(Actions.productsLoaded(res.data))
		} else if (isError(res)) {
			error = res.errors[0]
		}
	} catch (e) {
		const lang = getCurrentLanguage().locale
		const errorMessage = yield* call(loadTranslations, lang, 'react.products.load_error')
		error = new AukError(errorMessage, error, { action: { ...action } })
	}
	if (error) yield* put(Actions.productsFailed(error))
}

function* loadUpsells(action: Action<'UPSELL_LOAD'>) {
	const product = action.payload
	const variantIds = extractUpsells(product, action.meta).map(u => u.id)
	const lines = [product.variants[0].id, ...variantIds].map(merchandiseId => ({ merchandiseId, quantity: 1 }))

	try {
		const [cart, upsells] = yield* all([
			call(() => shopifyFetch(UpsellCheckoutCreateDocument, { lines })),
			call(() => shopifyFetch(UpsellDocument, { variantIds }))
		] as const)

		if (isOk(upsells)) yield* put(Actions.upsellsLoaded(upsells.data, product.id))
		else yield* put(Actions.upsellsLoaded([], product.id))
		if (isOk(cart)) yield* put(Actions.upsellsCartLoaded(cart.data))
	} catch {
		yield* put(Actions.upsellsLoaded([], product.id))
	}
}

function* onUpsellChange(action: Action<'UPSELL_ADD' | 'UPSELL_REMOVE'>) {
	const upsellCart = yield* select((s: State) => s.products.upsellCart)
	if (!upsellCart) return

	const upsellID = action.type === 'UPSELL_ADD' ? action.payload.id : action.meta
	const controller = new AbortController()
	try {
		let res
		switch (action.type) {
			case 'UPSELL_ADD':
				res = yield* call(() =>
					shopifyFetch(
						UpsellCartAddDocument,
						{ id: upsellCart.id, lines: [{ merchandiseId: upsellID, quantity: 1 }] },
						controller.signal
					)
				)
				break
			case 'UPSELL_REMOVE': {
				const line = upsellCart.lines.find(l => l.merchandise.id === upsellID)
				if (!line) return
				res = yield* call(() =>
					shopifyFetch(UpsellCartRemoveDocument, { id: upsellCart.id, lines: [line.id] }, controller.signal)
				)
				break
			}
		}

		if (isOk(res)) {
			yield* put(Actions.upsellsCartLoaded(res.data))
			const lines = res.data.lines
			if (lines) {
				for (const line of lines) {
					const discount = line.discountAllocations.reduce((a: number, b) => a + getDiscountValue(b), 0)
					yield* put(Actions.updateUpsell({ discounts: discount }, line.merchandise.id))
				}
			}
		}
	} finally {
		if (yield* cancelled()) {
			controller.abort()
		}
	}
}
